import { useState, useRef } from "react";
import { Upload, Download, FileText, AlertCircle, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Product, useProducts } from "@/hooks/useProducts";
import { useQueryClient } from "@tanstack/react-query";

interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
}

export function ProductImportExport() {
  const { data: products } = useProducts();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [previewData, setPreviewData] = useState<Record<string, string>[] | null>(null);

  const exportToCSV = () => {
    if (!products || products.length === 0) {
      toast.error("No products to export");
      return;
    }

    const headers = [
      "name",
      "description",
      "sku",
      "base_price",
      "currency",
      "category",
      "tier",
      "is_active",
    ];

    const csvContent = [
      headers.join(","),
      ...products.map((product) =>
        [
          `"${product.name.replace(/"/g, '""')}"`,
          `"${(product.description || "").replace(/"/g, '""')}"`,
          `"${product.sku || ""}"`,
          product.base_price,
          product.currency,
          `"${product.category || ""}"`,
          `"${product.tier || "standard"}"`,
          product.is_active ? "true" : "false",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `products_export_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();

    toast.success(`Exported ${products.length} products`);
  };

  const downloadTemplate = () => {
    const headers = [
      "name",
      "description",
      "sku",
      "base_price",
      "currency",
      "category",
      "tier",
      "is_active",
    ];

    const sampleData = [
      '"Enterprise Software License","Annual software license with support","ENT-001",4999.99,USD,Software,premium,true',
      '"Consulting Services","Professional consulting per hour","CONS-001",250,USD,Services,standard,true',
      '"Support Package","24/7 premium support","SUP-001",1999,USD,Support,premium,true',
    ];

    const csvContent = [headers.join(","), ...sampleData].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "products_import_template.csv";
    link.click();

    toast.success("Template downloaded");
  };

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.split("\n").filter((line) => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const rows: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values: string[] = [];
      let current = "";
      let inQuotes = false;

      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          values.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });
      rows.push(row);
    }

    return rows;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const data = parseCSV(text);

    if (data.length === 0) {
      toast.error("No valid data found in CSV");
      return;
    }

    setPreviewData(data);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!previewData || previewData.length === 0) return;

    setImporting(true);
    setImportProgress(0);

    const { data: profile } = await supabase
      .from("profiles")
      .select("tenant_id")
      .single();

    if (!profile?.tenant_id) {
      toast.error("No tenant found");
      setImporting(false);
      return;
    }

    const result: ImportResult = { success: 0, failed: 0, errors: [] };

    for (let i = 0; i < previewData.length; i++) {
      const row = previewData[i];

      try {
        if (!row.name) {
          throw new Error(`Row ${i + 1}: Name is required`);
        }

        const { error } = await supabase.from("products").insert({
          tenant_id: profile.tenant_id,
          name: row.name,
          description: row.description || null,
          sku: row.sku || null,
          base_price: parseFloat(row.base_price) || 0,
          currency: row.currency || "USD",
          category: row.category || null,
          tier: row.tier || "standard",
          is_active: row.is_active?.toLowerCase() !== "false",
        });

        if (error) throw error;
        result.success++;
      } catch (err: any) {
        result.failed++;
        result.errors.push(err.message || `Row ${i + 1}: Unknown error`);
      }

      setImportProgress(((i + 1) / previewData.length) * 100);
    }

    setImportResult(result);
    setImporting(false);

    if (result.success > 0) {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success(`Imported ${result.success} products`);
    }

    if (result.failed > 0) {
      toast.error(`${result.failed} products failed to import`);
    }
  };

  const resetImport = () => {
    setPreviewData(null);
    setImportResult(null);
    setImportProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex gap-2">
      <Button variant="outline" onClick={exportToCSV}>
        <Download className="h-4 w-4 mr-2" />
        Export CSV
      </Button>

      <Dialog open={importOpen} onOpenChange={(open) => {
        setImportOpen(open);
        if (!open) resetImport();
      }}>
        <DialogTrigger asChild>
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Products from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import products. Download the template for the correct format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <FileText className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-2">
                Drag and drop a CSV file, or click to browse
              </p>
              <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                Select File
              </Button>
            </div>

            {previewData && !importResult && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">{previewData.length} products found</Badge>
                  <Button variant="ghost" size="sm" onClick={resetImport}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                </div>

                <ScrollArea className="h-[200px] border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Name</th>
                        <th className="p-2 text-left">SKU</th>
                        <th className="p-2 text-right">Price</th>
                        <th className="p-2 text-left">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 10).map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{row.name}</td>
                          <td className="p-2 text-muted-foreground">{row.sku || "—"}</td>
                          <td className="p-2 text-right">${parseFloat(row.base_price || "0").toFixed(2)}</td>
                          <td className="p-2">{row.category || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.length > 10 && (
                    <p className="p-2 text-center text-muted-foreground text-xs">
                      ...and {previewData.length - 10} more
                    </p>
                  )}
                </ScrollArea>
              </div>
            )}

            {importing && (
              <div className="space-y-2">
                <Progress value={importProgress} />
                <p className="text-sm text-muted-foreground text-center">
                  Importing... {Math.round(importProgress)}%
                </p>
              </div>
            )}

            {importResult && (
              <div className="space-y-3">
                <div className="flex gap-4">
                  {importResult.success > 0 && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span>{importResult.success} imported</span>
                    </div>
                  )}
                  {importResult.failed > 0 && (
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertCircle className="h-5 w-5" />
                      <span>{importResult.failed} failed</span>
                    </div>
                  )}
                </div>

                {importResult.errors.length > 0 && (
                  <ScrollArea className="h-[100px] border rounded-lg p-2">
                    {importResult.errors.map((error, i) => (
                      <p key={i} className="text-sm text-destructive">{error}</p>
                    ))}
                  </ScrollArea>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>
              {importResult ? "Close" : "Cancel"}
            </Button>
            {previewData && !importResult && (
              <Button onClick={handleImport} disabled={importing}>
                {importing ? "Importing..." : `Import ${previewData.length} Products`}
              </Button>
            )}
            {importResult && (
              <Button onClick={resetImport}>Import More</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
